// src/App.jsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';

// Layouts
import AuthorLayout from './components/layouts/AuthorLayout';
import AdminLayout from './components/layouts/AdminLayout';
import ReviewerLayout from './components/layouts/ReviewerLayout';
import GuestLayout from './components/layouts/GuestLayout';

// Route Protection Components
import ProtectedRoute from './components/ProtectedRoute';

// Auth Components
import AdminLogin from './components/auth/AdminLogin';
import AuthorLogin from './components/auth/AuthorLogin';
import AuthorSignup from './components/auth/AuthorSignup';

// Author Components
import Dashboard from './components/author/Dashboard';
import ManuscriptsList from './components/author/ManuscriptsList';
import CoAuthoredManuscripts from './components/author/CoAuthoredManuscripts';
import ManuscriptsWithDecisions from './components/author/ManuscriptsWithDecisions';
import SubmissionWizard from './components/author/SubmissionWizard';
import AuthorReviews from './components/author/Reviews';

// Reviewer Components
import ReviewerDashboard from './components/reviewer/ReviewerDashboard';
import ReviewForm from './components/reviewer/ReviewForm';
import SubmittedReviews from './components/reviewer/SubmittedReviews';
import PendingReviews from './components/reviewer/PendingReview';
import OverdueReviews from './components/reviewer/OverdueReview';

// Admin Components
import AdminDashboard from './components/admin/Dashboard';
import SupportLogin from './components/support/SupportLogin';
import SupportDashboard from './components/support/SupportDashboard';
import SupportForgotPassword from './components/support/SupportForgotPassword';
import SupportVerifyResetCode from './components/support/SupportVerifyResetCode';
import SupportResetPassword from './components/support/SupportResetPassword';
import AdminSubmissions from './components/admin/Submissions';
import AdminCalendar from './components/admin/Calendar';
import AdminSettings from './components/admin/Settings';
import PersonnelInvitation from './components/admin/PersonnelInvitations';
import AcceptPaper from './components/admin/AcceptPaper';
import RejectPaper from './components/admin/RejectPaper';
import RevisePaper from './components/admin/RevisePaper';
import ReturnPaper from './components/admin/ReturnPaper';
import Authors from './components/admin/Authors';
import Newsletter from './components/admin/NewsLetter';
import EditorInvitations from './components/admin/EditorInvitations';
import InvitationsManagement from './components/admin/InitationsManagement';
import Decisioned from './components/admin/Decisioned';
import PendingDecisions from './components/admin/PendingDecisions';
import DecisionForm from './components/admin/DecisionForm';
import ManageSupplements from './components/admin/ManageSupplements';
import PublishManuscript from './components/admin/PublishManuscript';
import ArchivedSubmissions from './components/admin/ArchivedSubmissions';
import ManageAnnouncements from './components/admin/ManageAnnoouncements';
import ManageEditors from './components/admin/ManageEditors';
import ManageSpecialIssues from './components/admin/ManageSpecialIssues';
import EditorsManagement from './components/admin/EditorsManagement';

// Shared Components
import Inbox from './components/messages/Inbox';
import Settings from './components/Settings';
import ReviewContent from './components/common/ReviewContent';
import InvitationHandler from './components/InvitationHandler';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import PreviewIssue from './components/PreviewIssue';
import VerifyNotice from './components/auth/VerifyNotice';
import EmailVerification from './components/auth/EmailVerification';
import EditorialAssistantInvitation from './components/EditorialAssistantInvitation';

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          {/* Public Routes with Guest Layout */}
            <Route path="/contentMap" element={<PreviewIssue />} />
 
          <Route element={<GuestLayout />}>
            <Route path="/content" element={<PreviewIssue />} />


            <Route path="/portal/login" element={<AuthorLogin />} />
            <Route path="/portal/verify-notice" element={<VerifyNotice />} />
            <Route path="/portal/verify-email" element={<EmailVerification />} />

            <Route path="/portal/signup" element={<AuthorSignup />} />
            <Route path="/portal/forgot-password" element={<ForgotPassword />} />
            <Route path="/portal/reset-password" element={<ResetPassword />} />



            <Route path="/invitation/accept" element={<InvitationHandler />} />
            <Route path="/invitation/decline" element={<InvitationHandler />} />
            <Route path="/invitation/editorial-assistant/accept" element={<EditorialAssistantInvitation />} />


          </Route>
          
          {/* Admin Authentication */}
          <Route path="/editors/login" element={<AdminLogin />} />

          {/* Support Dashboard — uses its own JWT auth, independent of the main AuthProvider */}
          <Route path="/support/login" element={<SupportLogin />} />
          <Route path="/support/forgot-password" element={<SupportForgotPassword />} />
          <Route path="/support/verify-reset-code" element={<SupportVerifyResetCode />} />
          <Route path="/support/reset-password" element={<SupportResetPassword />} />
          <Route path="/support/dashboard" element={<SupportDashboard />} />

          {/* Author Routes - Modified to check for author access permission instead of strict role */}
          <Route 
            path="/" 
            element={
              <ProtectedRoute requireAuth={true}>
                <AuthorLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="manuscripts" element={<ManuscriptsList />} />
            <Route path="coauthored" element={<CoAuthoredManuscripts />} />
            <Route path="inreview" element={<ManuscriptsWithDecisions />} />
            <Route path="submit" element={<SubmissionWizard />} />
            <Route path="reviews" element={<AuthorReviews />} />
            <Route path="messages/inbox" element={<Inbox />} />
            <Route path="settings" element={<Settings />} />
          </Route>

          {/* Reviewer Routes - Modified to check for reviewer access permission */}
          <Route 
            path="/reviewerdash" 
            element={
              <ProtectedRoute requireAuth={true}>
                <ReviewerLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ReviewerDashboard />} />
            <Route path="pending" element={<PendingReviews />} />
            <Route path="overdue" element={<OverdueReviews />} />
            <Route path="review/:manuscriptId" element={<ReviewForm />} />
            <Route path="submitted" element={<SubmittedReviews />} />
            <Route path="messages/inbox" element={<Inbox />} />
            <Route path="review/content/:reviewId/:articleId" element={<ReviewContent />} />

          </Route>

          {/* Admin Routes - Modified to check for editor access permission */}
          <Route 
            path="/editors" 
            element={
              <ProtectedRoute requireAuth={true}>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/editors/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="admin/authors" element={<Authors />} />
            <Route path="process/invite" element={<PersonnelInvitation />} />
            <Route path="process/accept" element={<AcceptPaper />} />
            <Route path="process/reject" element={<RejectPaper />} />
            <Route path="process/revise" element={<RevisePaper />} />
            <Route path="process/return" element={<ReturnPaper />} />
            <Route path="admin/newsletter" element={<Newsletter />} />
            <Route path="editor-invitations" element={<EditorInvitations />} />
            <Route path="admin/invitations/management" element={<InvitationsManagement />} />
            <Route path="admin/archived" element={<ArchivedSubmissions />} />
            <Route path="decisioned" element={<Decisioned />} />
            <Route path="submitted-reviews" element={<PendingDecisions />} />
            <Route path="decision/:articleId" element={<DecisionForm />} />
            <Route path="admin/manage-publications" element={<ManageSupplements />} />
            <Route path="admin/manage-issues" element={<ManageSupplements />} />
            <Route path="admin/publish" element={<PublishManuscript />} />
            <Route path="admin/announcements" element={<ManageAnnouncements />} />
            <Route path="admin/manage-editors" element={<ManageEditors />} />
            <Route path="editors-management" element={<EditorsManagement />} />
            <Route path="admin/manage-special-issues" element={<ManageSpecialIssues />} />
            <Route path="submissions" element={<AdminSubmissions />} />
            <Route path="reviews" element={<SubmittedReviews />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="inbox" element={<Inbox />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="review/content/:reviewId/:articleId" element={<ReviewContent />} />
          </Route>

          {/* Fallback Routes */}
          <Route path="editors" element={<Navigate to="/editors/dashboard" replace />} />

          <Route path="/login" element={<Navigate to="/portal/login" replace />} />
          <Route path="/register" element={<Navigate to="/portal/signup" replace />} />
          <Route path="/signup" element={<Navigate to="/portal/signup" replace />} />


          <Route path="*" element={<Navigate to="/portal/login" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;